// Slots 1-11 are the starting XI; 12-15 are the bench.
const STARTING_XI_MAX_SLOT = 11;

type SwapEndpoint = {
  slot: number;
  player: { id: string; isCaptain: boolean; isViceCaptain: boolean };
};

export type Captaincy = { captainId: string; viceCaptainId: string };

/**
 * When a substitution moves a starter to the bench and that starter wears the
 * armband, the incoming player inherits it so the captain stays in the starting
 * XI. FPL allows benching the captain (the vice takes over), but the planner API
 * rejects a benched captain (CAPTAIN_NOT_IN_XI), so the armband is reassigned in
 * the same request. Mirrors scoreline-app's swapPlayers.
 *
 * Returns the new captain/vice ids to batch with the SWAP, or undefined when no
 * reassignment is needed (a reorder within the XI or bench, or the benched
 * player wears no armband).
 */
export const resolveSwapCaptaincy = (
  source: SwapEndpoint,
  target: SwapEndpoint,
  currentCaptainId: string | null,
  currentViceCaptainId: string | null
): Captaincy | undefined => {
  const sourceStarts = source.slot <= STARTING_XI_MAX_SLOT;
  const targetStarts = target.slot <= STARTING_XI_MAX_SLOT;
  // Only a starter-for-bench swap benches someone.
  if (sourceStarts === targetStarts) return undefined;

  const benched = sourceStarts ? source : target;
  const incoming = sourceStarts ? target : source;
  if (!benched.player.isCaptain && !benched.player.isViceCaptain) {
    return undefined;
  }
  if (!currentCaptainId || !currentViceCaptainId) return undefined;

  return {
    captainId: benched.player.isCaptain ? incoming.player.id : currentCaptainId,
    viceCaptainId: benched.player.isViceCaptain
      ? incoming.player.id
      : currentViceCaptainId
  };
};
